// ============================================
// COMPONENTE: Chat de Suporte Inteligente Sophya
// ============================================
// 3 Camadas: RAG → IA+KB → IA+MCP
// Ações: Cadastro paciente, Agendamento consulta
// Autocomplete FAQ, Feedback, WhatsApp fallback
// ============================================

import { useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';

interface Mensagem {
  id: string;
  tipo: 'usuario' | 'assistente' | 'sistema';
  conteudo: string;
  timestamp: Date;
  camada?: string;
  confianca?: number;
  fontes?: string[];
  acoes?: Array<{ label: string; acao: string; estilo?: string; dados?: any }>;
  sugestoes?: string[];
}

interface FAQSugestao {
  pergunta: string;
  resposta: string;
  categoria: string;
}

const WHATSAPP_NUMERO = '5511936189517';

const BOAS_VINDAS: Mensagem = {
  id: 'welcome', tipo: 'assistente',
  conteudo: `Olá! Sou a **Sophya** 🤖\n\nPosso ajudar com:\n• Dúvidas sobre o sistema\n• **Cadastrar pacientes** direto pelo chat\n• **Agendar consultas**\n• **Consultas do dia**, pagamentos e mais\n\nDigite sua dúvida ou escolha uma ação rápida!`,
  timestamp: new Date(),
  sugestoes: ['Consultas de hoje', 'Pagamentos pendentes', 'Cadastrar paciente', 'Agendar consulta']
};

type Estado = 'livre' | 'aguardando_feedback' | 'coletando_dados';

interface DadosColeta {
  acao: string;
  campos: Record<string, string>;
  etapa: number;
}

export default function SupportChat() {
  const [aberto, setAberto] = useState(false);
  const [msgs, setMsgs] = useState<Mensagem[]>([BOAS_VINDAS]);
  const [input, setInput] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [minimizado, setMinimizado] = useState(false);
  const [estado, setEstado] = useState<Estado>('livre');
  const [faqSugestoes, setFaqSugestoes] = useState<FAQSugestao[]>([]);
  const [coleta, setColeta] = useState<DadosColeta | null>(null);
  const [ultimaPergunta, setUltimaPergunta] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);
  useEffect(() => { if (aberto && !minimizado) setTimeout(() => inputRef.current?.focus(), 100); }, [aberto, minimizado]);

  // Autocomplete FAQ
  const buscarSugestoes = useCallback(async (texto: string) => {
    if (texto.length < 3) { setFaqSugestoes([]); return; }
    try {
      const r = await api.get('/sophya-chat/suggest', { params: { q: texto } });
      if (r.data?.success) setFaqSugestoes(r.data.data.slice(0, 4));
    } catch { setFaqSugestoes([]); }
  }, []);

  const onInputChange = (valor: string) => {
    setInput(valor);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscarSugestoes(valor), 300);
  };

  const addMsg = (msg: Omit<Mensagem, 'id' | 'timestamp'>) => {
    const nova: Mensagem = { ...msg, id: Date.now().toString() + Math.random(), timestamp: new Date() };
    setMsgs(prev => [...prev, nova]);
    return nova;
  };

  // ========================================
  // COLETA DE DADOS PARA AÇÕES
  // ========================================
  const iniciarColeta = (acao: string) => {
    if (acao === 'cadastrar_paciente') {
      setColeta({ acao, campos: {}, etapa: 0 });
      setEstado('coletando_dados');
      addMsg({ tipo: 'assistente', conteudo: '👤 Vamos cadastrar um paciente!\n\nQual é o **nome completo**?' });
    } else if (acao === 'agendar_consulta') {
      setColeta({ acao, campos: {}, etapa: 0 });
      setEstado('coletando_dados');
      addMsg({ tipo: 'assistente', conteudo: '📅 Vamos agendar uma consulta!\n\nQual é o **nome do paciente**?' });
    }
  };

  const processarColeta = async (texto: string) => {
    if (!coleta) return;
    const c = { ...coleta };

    if (c.acao === 'cadastrar_paciente') {
      if (c.etapa === 0) { c.campos.nome = texto; c.etapa = 1; setColeta(c); addMsg({ tipo: 'assistente', conteudo: `Nome: **${texto}**\n\nQual o **telefone/WhatsApp**? (ou "pular")` }); return; }
      if (c.etapa === 1) { c.campos.telefone = texto === 'pular' ? '' : texto; c.etapa = 2; setColeta(c); addMsg({ tipo: 'assistente', conteudo: 'Qual o **CPF**? (ou "pular")' }); return; }
      if (c.etapa === 2) { c.campos.cpf = texto === 'pular' ? '' : texto; c.etapa = 3; setColeta(c); addMsg({ tipo: 'assistente', conteudo: 'E o **email**? (ou "pular")' }); return; }
      if (c.etapa === 3) { c.campos.email = texto === 'pular' ? '' : texto; c.etapa = 4; setColeta(c); addMsg({ tipo: 'assistente', conteudo: '**Data de nascimento**? (formato DD/MM/AAAA ou "pular")' }); return; }
      if (c.etapa === 4) { c.campos.data_nascimento = texto === 'pular' ? '' : texto; c.etapa = 5; setColeta(c); addMsg({ tipo: 'assistente', conteudo: '**Tipo de serviço**?\n• psicoterapia\n• avaliacao\n• orientacao\n• terapia_casal\n• triagem\n\n(ou "pular" para psicoterapia)' }); return; }
      if (c.etapa === 5) {
        c.campos.servico = texto === 'pular' ? 'psicoterapia' : texto;
        addMsg({ tipo: 'sistema', conteudo: `Confirma o cadastro?\n• Nome: **${c.campos.nome}**\n• Tel: ${c.campos.telefone || 'N/A'}\n• CPF: ${c.campos.cpf || 'N/A'}\n• Email: ${c.campos.email || 'N/A'}\n• Nascimento: ${c.campos.data_nascimento || 'N/A'}\n• Serviço: ${c.campos.servico}`,
          acoes: [
            { label: '✅ Confirmar', acao: 'confirmar_cadastro', estilo: 'btn-success', dados: c.campos },
            { label: '❌ Cancelar', acao: 'cancelar_coleta', estilo: 'btn-outline-danger' }
          ]
        });
        setEstado('aguardando_feedback');
        return;
      }
    }

    if (c.acao === 'agendar_consulta') {
      if (c.etapa === 0) { c.campos.paciente_nome = texto; c.etapa = 1; setColeta(c); addMsg({ tipo: 'assistente', conteudo: `Paciente: **${texto}**\n\nQual a **data**? (formato: AAAA-MM-DD)` }); return; }
      if (c.etapa === 1) { c.campos.data = texto; c.etapa = 2; setColeta(c); addMsg({ tipo: 'assistente', conteudo: 'Qual o **horário**? (formato: HH:MM)' }); return; }
      if (c.etapa === 2) {
        c.campos.hora = texto;
        addMsg({ tipo: 'sistema', conteudo: `Confirma o agendamento?\n• Paciente: **${c.campos.paciente_nome}**\n• Data: **${c.campos.data}**\n• Hora: **${c.campos.hora}**`,
          acoes: [
            { label: '✅ Confirmar', acao: 'confirmar_agendamento', estilo: 'btn-success', dados: c.campos },
            { label: '❌ Cancelar', acao: 'cancelar_coleta', estilo: 'btn-outline-danger' }
          ]
        });
        setEstado('aguardando_feedback');
        return;
      }
    }
  };

  // ========================================
  // ENVIAR MENSAGEM (3 CAMADAS)
  // ========================================
  const enviarMensagem = async () => {
    if (!input.trim() || enviando) return;
    const texto = input.trim();
    addMsg({ tipo: 'usuario', conteudo: texto });
    setInput(''); setFaqSugestoes([]); setUltimaPergunta(texto);

    if (estado === 'coletando_dados') { processarColeta(texto); return; }

    setEnviando(true);
    try {
      const historico = msgs.filter(m => m.tipo !== 'sistema').slice(-6).map(m => ({ role: m.tipo === 'usuario' ? 'user' as const : 'assistant' as const, content: m.conteudo }));
      const r = await api.post('/sophya-chat/message', { message: texto, historico });

      if (r.data?.success && r.data.data) {
        const d = r.data.data;
        const respMsg: Mensagem = {
          id: Date.now().toString(), tipo: 'assistente', conteudo: d.resposta,
          timestamp: new Date(), camada: d.camada, confianca: d.confianca,
          fontes: d.fontes, sugestoes: d.sugestoes
        };
        setMsgs(prev => [...prev, respMsg]);

        // Feedback
        setTimeout(() => {
          addMsg({ tipo: 'sistema', conteudo: 'Essa resposta ajudou?',
            acoes: [
              { label: '👍 Sim!', acao: 'feedback_positivo', estilo: 'btn-success' },
              { label: '👎 Não', acao: 'feedback_negativo', estilo: 'btn-outline-secondary' },
              { label: '💬 WhatsApp', acao: 'whatsapp', estilo: 'btn-outline-primary' }
            ]
          });
          setEstado('aguardando_feedback');
        }, 600);
      } else {
        addMsg({ tipo: 'assistente', conteudo: 'Desculpe, não consegui processar sua pergunta 😕',
          acoes: [{ label: '💬 WhatsApp', acao: 'whatsapp', estilo: 'btn-success' }, { label: '🔄 Tentar novamente', acao: 'retry', estilo: 'btn-outline-secondary' }]
        });
        setEstado('aguardando_feedback');
      }
    } catch (err) {
      console.error('[SupportChat] Erro:', err);
      addMsg({ tipo: 'assistente', conteudo: 'IA indisponível no momento 😕',
        acoes: [{ label: '💬 WhatsApp', acao: 'whatsapp', estilo: 'btn-success' }, { label: '🔄 Tentar', acao: 'retry', estilo: 'btn-outline-secondary' }]
      });
      setEstado('aguardando_feedback');
    } finally { setEnviando(false); }
  };

  // ========================================
  // HANDLER DE AÇÕES
  // ========================================
  const handleAcao = async (acao: string, dados?: any) => {
    if (acao === 'feedback_positivo') {
      addMsg({ tipo: 'sistema', conteudo: '🎉 Que bom! Se precisar de mais algo, é só perguntar!' });
      setEstado('livre');
      try { await api.post('/sophya-chat/feedback', { util: true }); } catch {}
    } else if (acao === 'feedback_negativo') {
      addMsg({ tipo: 'sistema', conteudo: 'Posso tentar de outro jeito. Reformule sua pergunta ou fale com o suporte.' });
      setEstado('livre');
      try { await api.post('/sophya-chat/feedback', { util: false }); } catch {}
    } else if (acao === 'whatsapp') {
      const t = encodeURIComponent(`Olá! Preciso de ajuda com o Sophya.\n\nDúvida: ${ultimaPergunta}`);
      window.open(`https://api.whatsapp.com/send?phone=${WHATSAPP_NUMERO}&text=${t}`, '_blank');
      addMsg({ tipo: 'sistema', conteudo: '📱 WhatsApp aberto! Nosso time responderá em breve.' });
      setEstado('livre');
    } else if (acao === 'retry') {
      setEstado('livre'); setTimeout(() => inputRef.current?.focus(), 100);
    } else if (acao === 'cadastrar_paciente' || acao === 'Cadastrar paciente' || acao === 'Cadastrar paciente agora') {
      iniciarColeta('cadastrar_paciente');
    } else if (acao === 'agendar_consulta' || acao === 'Agendar consulta' || acao === 'Agendar consulta agora') {
      iniciarColeta('agendar_consulta');
    } else if (acao === 'confirmar_cadastro') {
      setEnviando(true);
      try {
        const r = await api.post('/sophya-chat/action', { tipo: 'cadastrar_paciente', dados });
        addMsg({ tipo: 'assistente', conteudo: r.data?.data?.mensagem || 'Cadastro processado.' });
      } catch { addMsg({ tipo: 'assistente', conteudo: 'Erro ao cadastrar paciente.' }); }
      setColeta(null); setEstado('livre'); setEnviando(false);
    } else if (acao === 'confirmar_agendamento') {
      setEnviando(true);
      try {
        const r = await api.post('/sophya-chat/action', { tipo: 'agendar_consulta', dados });
        addMsg({ tipo: 'assistente', conteudo: r.data?.data?.mensagem || 'Agendamento processado.' });
      } catch { addMsg({ tipo: 'assistente', conteudo: 'Erro ao agendar consulta.' }); }
      setColeta(null); setEstado('livre'); setEnviando(false);
    } else if (acao === 'cancelar_coleta') {
      addMsg({ tipo: 'sistema', conteudo: 'Ação cancelada. Como posso ajudar?' });
      setColeta(null); setEstado('livre');
    } else {
      // Sugestão rápida → enviar como mensagem
      setInput(acao); setEstado('livre');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const limparChat = () => { setMsgs([BOAS_VINDAS]); setEstado('livre'); setColeta(null); };

  const fmt = (t: string): string => t
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br>').replace(/•/g, '&bull;');

  const camadaLabel = (c?: string) => {
    if (c === 'rag') return '📚 Base';
    if (c === 'ia_kb') return '🤖 IA';
    if (c === 'ia_mcp') return '🔧 Dados';
    if (c === 'acao') return '⚡ Ação';
    return '';
  };

  return (
    <>
      {/* Botão flutuante */}
      <button
        className="btn btn-primary rounded-circle shadow-lg d-flex align-items-center justify-content-center"
        onClick={() => { if (aberto && !minimizado) setAberto(false); else { setAberto(true); setMinimizado(false); } }}
        title="Suporte Sophya"
        style={{ position: 'fixed', bottom: '20px', right: '20px', width: '56px', height: '56px', zIndex: 9999, border: 'none', transition: 'all 0.3s ease', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        onMouseEnter={e => (e.currentTarget.style.transform = 'scale(1.12)')}
        onMouseLeave={e => (e.currentTarget.style.transform = 'scale(1)')}
      >
        <i className={`fas ${aberto ? 'fa-times' : 'fa-headset'}`} style={{ fontSize: aberto ? '20px' : '22px' }} />
      </button>

      {/* Janela do chat */}
      {aberto && (
        <div className="card shadow-lg" style={{ position: 'fixed', bottom: '86px', right: '20px', width: '400px', maxWidth: 'calc(100vw - 40px)', height: minimizado ? 'auto' : '560px', maxHeight: 'calc(100vh - 120px)', zIndex: 9998, borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column', animation: 'slideUp 0.3s ease' }}>

          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', cursor: 'pointer', minHeight: '52px', padding: '10px 16px' }}
            className="text-white d-flex align-items-center" onClick={() => setMinimizado(!minimizado)}>
            <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '10px' }}>
              <i className="fas fa-robot" style={{ fontSize: '16px' }} />
            </div>
            <div className="flex-grow-1">
              <div className="fw-bold" style={{ fontSize: '14px' }}>Sophya IA</div>
              <small className="opacity-75" style={{ fontSize: '11px' }}>
                {enviando ? '✍️ Digitando...' : estado === 'coletando_dados' ? '📝 Coletando dados...' : '🟢 Online • 3 camadas'}
              </small>
            </div>
            <div className="d-flex gap-1">
              <button className="btn btn-sm text-white p-0" onClick={e => { e.stopPropagation(); limparChat(); }} title="Nova conversa" style={{ width: '24px', height: '24px' }}>
                <i className="fas fa-plus" style={{ fontSize: '12px' }} />
              </button>
              <button className="btn btn-sm text-white p-0" onClick={e => { e.stopPropagation(); setMinimizado(!minimizado); }} style={{ width: '24px', height: '24px' }}>
                <i className={`fas fa-chevron-${minimizado ? 'up' : 'down'}`} style={{ fontSize: '12px' }} />
              </button>
            </div>
          </div>

          {/* Corpo */}
          {!minimizado && (<>
            <div className="flex-grow-1 overflow-auto p-3" style={{ backgroundColor: '#f0f2f5' }}>
              {msgs.map(msg => {
                const isUser = msg.tipo === 'usuario';
                const isSys = msg.tipo === 'sistema';
                return (
                  <div key={msg.id}>
                    <div className={`d-flex mb-2 ${isUser ? 'justify-content-end' : 'justify-content-start'}`}>
                      <div className={`rounded-3 px-3 py-2 ${isUser ? 'text-white' : isSys ? 'bg-warning bg-opacity-10 border border-warning' : 'bg-white border'}`}
                        style={{ maxWidth: '85%', minWidth: '80px', fontSize: '13px', lineHeight: '1.5', wordBreak: 'break-word',
                          ...(isUser ? { background: 'linear-gradient(135deg, #667eea, #764ba2)' } : {}),
                          borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px'
                        }}>
                        {!isUser && (
                          <div className="d-flex align-items-center mb-1">
                            <small className={`fw-bold ${isSys ? 'text-warning' : 'text-primary'}`} style={{ fontSize: '11px' }}>
                              {isSys ? '⚙️ Sistema' : '🤖 Sophya'}
                            </small>
                            {msg.camada && <span className="ms-auto badge bg-light text-dark" style={{ fontSize: '9px', padding: '1px 6px' }}>{camadaLabel(msg.camada)}</span>}
                          </div>
                        )}
                        <div dangerouslySetInnerHTML={{ __html: fmt(msg.conteudo) }} />
                        {msg.fontes && msg.fontes.length > 0 && (
                          <div className="mt-1"><small className="text-muted" style={{ fontSize: '10px' }}>📄 {msg.fontes.join(', ')}</small></div>
                        )}
                        <div className="text-end mt-1">
                          <small className={isUser ? 'opacity-75' : 'text-muted'} style={{ fontSize: '10px' }}>
                            {msg.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </small>
                        </div>
                      </div>
                    </div>

                    {/* Botões de ação */}
                    {msg.acoes && msg.acoes.length > 0 && (
                      <div className="d-flex flex-wrap justify-content-start mb-2 gap-1 ps-2">
                        {msg.acoes.map((a, i) => (
                          <button key={i} className={`btn btn-sm ${a.estilo || 'btn-outline-primary'}`}
                            onClick={() => handleAcao(a.acao, a.dados)}
                            style={{ fontSize: '11px', borderRadius: '16px', padding: '3px 10px' }}>
                            {a.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Sugestões rápidas inline */}
                    {msg.sugestoes && msg.sugestoes.length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mb-2 ps-2">
                        {msg.sugestoes.map((s, i) => (
                          <button key={i} className="btn btn-sm btn-outline-primary rounded-pill"
                            onClick={() => handleAcao(s)}
                            style={{ fontSize: '11px', padding: '2px 10px' }}>
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {enviando && (
                <div className="d-flex justify-content-start mb-2">
                  <div className="bg-white border rounded-3 px-3 py-2">
                    <div className="d-flex align-items-center gap-1">
                      <span className="spinner-grow spinner-grow-sm text-primary" style={{ width: '6px', height: '6px' }} />
                      <span className="spinner-grow spinner-grow-sm text-primary" style={{ width: '6px', height: '6px', animationDelay: '0.15s' }} />
                      <span className="spinner-grow spinner-grow-sm text-primary" style={{ width: '6px', height: '6px', animationDelay: '0.3s' }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Autocomplete FAQ */}
            {faqSugestoes.length > 0 && estado === 'livre' && (
              <div className="border-top px-3 py-2" style={{ backgroundColor: '#fefefe', maxHeight: '120px', overflowY: 'auto' }}>
                {faqSugestoes.map((faq, i) => (
                  <button key={i} className="btn btn-sm btn-light text-start w-100 mb-1 rounded-2"
                    onClick={() => { setInput(faq.pergunta); setFaqSugestoes([]); setTimeout(() => inputRef.current?.focus(), 50); }}
                    style={{ fontSize: '12px', padding: '4px 8px', border: '1px solid #e9ecef' }}>
                    <i className="fas fa-search text-muted me-1" style={{ fontSize: '10px' }} />
                    {faq.pergunta}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="px-3 py-2 border-top bg-white">
              {estado === 'aguardando_feedback' ? (
                <div className="text-center py-1">
                  <small className="text-muted" style={{ fontSize: '12px' }}>
                    <i className="fas fa-hand-point-up me-1" /> Use os botões acima
                  </small>
                </div>
              ) : (
                <div className="input-group input-group-sm">
                  <input ref={inputRef} type="text" className="form-control" value={input}
                    placeholder={estado === 'coletando_dados' ? 'Digite o dado solicitado...' : 'Digite sua dúvida...'}
                    onChange={e => onInputChange(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && enviarMensagem()}
                    disabled={enviando}
                    style={{ borderRadius: '20px 0 0 20px', fontSize: '13px' }}
                  />
                  <button className="btn btn-primary" onClick={enviarMensagem}
                    disabled={enviando || !input.trim()}
                    style={{ borderRadius: '0 20px 20px 0', background: 'linear-gradient(135deg, #667eea, #764ba2)', border: 'none' }}>
                    {enviando
                      ? <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }} />
                      : <i className="fas fa-paper-plane" style={{ fontSize: '12px' }} />}
                  </button>
                </div>
              )}
            </div>
          </>)}
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
